/**
 * IndexedDB Service for GIS Layer Caching
 * Provides persistent storage with user-specific data separation
 */

const DB_NAME = 'MAUD_GIS_Cache';
const DB_VERSION = 2; // Incremented to add SEARCH_HISTORY store

// Object store names
const STORES = {
  LAYER_DATA: 'layerData',
  LAYER_STYLES: 'layerStyles',
  BASE_LAYERS: 'baseLayers',
  DATA_LAYERS: 'dataLayers',
  USER_PREFS: 'userPreferences',
  SEARCH_HISTORY: 'searchHistory'
};

interface CacheEntry<T> {
  userId: string;
  key: string;
  data: T;
  timestamp: number;
  expiresAt?: number;
}

class IndexedDBService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;
  private currentUserId: string = 'default';
  private initAttempts: number = 0;
  private maxInitAttempts: number = 2;

  constructor() {
    this.initPromise = this.initDB();
  }

  /**
   * Initialize IndexedDB
   */
  private async initDB(): Promise<void> {
    // console.log('[IndexedDB] initDB starting...');

    try {
      await this.tryOpenDB();
    } catch (error) {
      // If first attempt fails due to timeout or blocked, try deleting and recreating
      if (this.initAttempts < this.maxInitAttempts) {
        // console.log('[IndexedDB] First attempt failed, trying to delete and recreate database...');
        this.initAttempts++;
        try {
          await this.deleteDatabase();
          await this.tryOpenDB();
        } catch (retryError) {
          console.error('[IndexedDB] Retry also failed:', retryError);
          throw retryError;
        }
      } else {
        throw error;
      }
    }
  }

  /**
   * Delete the database
   */
  private deleteDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      // console.log('[IndexedDB] Deleting database...');
      const deleteRequest = indexedDB.deleteDatabase(DB_NAME);

      deleteRequest.onsuccess = () => {
        // console.log('[IndexedDB] Database deleted successfully');
        resolve();
      };

      deleteRequest.onerror = () => {
        console.error('[IndexedDB] Error deleting database:', deleteRequest.error);
        // Still resolve - we'll try to create fresh
        resolve();
      };

      deleteRequest.onblocked = () => {
        console.warn('[IndexedDB] Database deletion blocked, proceeding anyway...');
        // Give it a moment then resolve
        setTimeout(resolve, 500);
      };
    });
  }

  /**
   * Try to open the database
   */
  private tryOpenDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.indexedDB) {
        console.warn('[IndexedDB] IndexedDB not supported in this environment');
        reject(new Error('IndexedDB not supported'));
        return;
      }

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        console.error('[IndexedDB] Database open timed out after 5 seconds');
        reject(new Error('IndexedDB open timed out'));
      }, 5000);

      // console.log('[IndexedDB] Opening database:', DB_NAME, 'version:', DB_VERSION);
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        clearTimeout(timeout);
        console.error('[IndexedDB] Error opening database:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        clearTimeout(timeout);
        this.db = request.result;
        // console.log('[IndexedDB] Database opened successfully');
        // console.log('[IndexedDB] Available stores:', Array.from(this.db.objectStoreNames));
        resolve();
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        // console.log('[IndexedDB] Upgrading database schema from version', event.oldVersion, 'to', event.newVersion);

        // Create object stores
        Object.values(STORES).forEach((storeName) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: ['userId', 'key'] });

            // Create indexes for efficient querying
            store.createIndex('userId', 'userId', { unique: false });
            store.createIndex('timestamp', 'timestamp', { unique: false });
            store.createIndex('expiresAt', 'expiresAt', { unique: false });

            // console.log(`[IndexedDB] Created object store: ${storeName}`);
          }
        });
      };

      request.onblocked = () => {
        clearTimeout(timeout);
        console.warn('[IndexedDB] Database upgrade blocked. Please close other tabs using this site.');
        // Still try to resolve if we have a db instance
        if (request.result) {
          this.db = request.result;
          resolve();
        } else {
          reject(new Error('Database upgrade blocked'));
        }
      };
    });
  }

  /**
   * Ensure database is initialized
   */
  private async ensureDB(): Promise<IDBDatabase> {
    // console.log('[IndexedDB] ensureDB called, db exists:', !!this.db);
    if (this.db) {
      return this.db;
    }

    if (this.initPromise) {
      // console.log('[IndexedDB] Waiting for initPromise...');
      try {
        await this.initPromise;
        // console.log('[IndexedDB] initPromise resolved');
      } catch (error) {
        // Don't retry - just fail fast and let the caller handle it
        console.error('[IndexedDB] initPromise failed:', error);
        throw error;
      }
    }

    if (!this.db) {
      console.error('[IndexedDB] Database still null after init');
      throw new Error('Failed to initialize IndexedDB');
    }

    // console.log('[IndexedDB] ensureDB returning db');
    return this.db;
  }

  /**
   * Set current user ID for user-specific storage
   */
  setUserId(userId: string): void {
    this.currentUserId = userId || 'default';
    // console.log(`[IndexedDB] Current user set to: ${this.currentUserId}`);
  }

  /**
   * Get current user ID
   */
  getUserId(): string {
    return this.currentUserId;
  }

  /**
   * Store data in IndexedDB
   */
  async set<T>(storeName: string, key: string, data: T, ttl?: number): Promise<void> {
    try {
      const db = await this.ensureDB();

      const entry: CacheEntry<T> = {
        userId: this.currentUserId,
        key,
        data,
        timestamp: Date.now(),
        expiresAt: ttl ? Date.now() + ttl : undefined
      };

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.put(entry);

        request.onsuccess = () => {
          // console.log(`[IndexedDB] Stored ${storeName}/${key} for user ${this.currentUserId}`);
          resolve();
        };

        request.onerror = () => {
          console.error(`[IndexedDB] Error storing ${storeName}/${key}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`[IndexedDB] Failed to store data:`, error);
      throw error;
    }
  }

  /**
   * Retrieve data from IndexedDB
   */
  async get<T>(storeName: string, key: string, userId?: string): Promise<T | null> {
    try {
      const db = await this.ensureDB();
      const targetUserId = userId || this.currentUserId;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.get([targetUserId, key]);

        request.onsuccess = () => {
          const entry = request.result as CacheEntry<T> | undefined;

          if (!entry) {
            // console.log(`[IndexedDB] Cache miss: ${storeName}/${key} for user ${targetUserId}`);
            resolve(null);
            return;
          }

          // Check if entry has expired
          if (entry.expiresAt && entry.expiresAt < Date.now()) {
            // console.log(`[IndexedDB] Cache expired: ${storeName}/${key}`);
            // Delete expired entry
            this.delete(storeName, key, targetUserId);
            resolve(null);
            return;
          }

          // console.log(`[IndexedDB] Cache hit: ${storeName}/${key} for user ${targetUserId}`);
          resolve(entry.data);
        };

        request.onerror = () => {
          console.error(`[IndexedDB] Error retrieving ${storeName}/${key}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`[IndexedDB] Failed to retrieve data:`, error);
      return null;
    }
  }

  /**
   * Retrieve all data from a store for current user
   */
  async getAll<T>(storeName: string, userId?: string): Promise<T[]> {
    // console.log(`[IndexedDB] getAll called for store: ${storeName}`);
    try {
      const db = await this.ensureDB();
      // console.log(`[IndexedDB] Database obtained, checking for store: ${storeName}`);
      // console.log(`[IndexedDB] Available stores:`, Array.from(db.objectStoreNames));
      const targetUserId = userId || this.currentUserId;

      // Check if store exists before trying to access it
      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`[IndexedDB] Store ${storeName} does not exist. Returning empty array.`);
        console.warn(`[IndexedDB] To fix this, clear IndexedDB in browser DevTools and reload.`);
        return [];
      }

      // console.log(`[IndexedDB] Store ${storeName} exists, creating transaction...`);

      return new Promise((resolve) => {
        try {
          const transaction = db.transaction([storeName], 'readonly');
          const store = transaction.objectStore(storeName);
          const index = store.index('userId');
          const request = index.openCursor(IDBKeyRange.only(targetUserId));
          const results: T[] = [];

          transaction.onerror = () => {
            console.error(`[IndexedDB] Transaction error for ${storeName}:`, transaction.error);
            resolve([]);
          };

          transaction.oncomplete = () => {
            // console.log(`[IndexedDB] Transaction completed for ${storeName}, found ${results.length} items`);
          };

          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
            if (cursor) {
              const entry = cursor.value as CacheEntry<T>;
              // Check if entry has expired
              if (!entry.expiresAt || entry.expiresAt >= Date.now()) {
                results.push(entry.data);
              }
              cursor.continue();
            } else {
              // Sort by timestamp descending (most recent first)
              results.sort((a: any, b: any) => (b.timestamp || 0) - (a.timestamp || 0));
              // console.log(`[IndexedDB] Returning ${results.length} items from ${storeName}`);
              resolve(results);
            }
          };

          request.onerror = () => {
            console.error(`[IndexedDB] Cursor error for ${storeName}:`, request.error);
            resolve([]);
          };
        } catch (innerError) {
          console.error(`[IndexedDB] Error creating transaction for ${storeName}:`, innerError);
          resolve([]);
        }
      });
    } catch (error) {
      console.error(`[IndexedDB] Failed to retrieve all data:`, error);
      return [];
    }
  }

  /**
   * Check if a key exists in the cache
   */
  async has(storeName: string, key: string, userId?: string): Promise<boolean> {
    try {
      const data = await this.get(storeName, key, userId);
      return data !== null;
    } catch (error) {
      return false;
    }
  }

  /**
   * Delete a specific entry
   */
  async delete(storeName: string, key: string, userId?: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const targetUserId = userId || this.currentUserId;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.delete([targetUserId, key]);

        request.onsuccess = () => {
          // console.log(`[IndexedDB] Deleted ${storeName}/${key} for user ${targetUserId}`);
          resolve();
        };

        request.onerror = () => {
          console.error(`[IndexedDB] Error deleting ${storeName}/${key}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`[IndexedDB] Failed to delete data:`, error);
    }
  }

  /**
   * Clear all entries in a store for current user
   */
  async clearStore(storeName: string, userId?: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      const targetUserId = userId || this.currentUserId;

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const index = store.index('userId');
        const request = index.openCursor(IDBKeyRange.only(targetUserId));

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          } else {
            // console.log(`[IndexedDB] Cleared ${storeName} for user ${targetUserId}`);
            resolve();
          }
        };

        request.onerror = () => {
          console.error(`[IndexedDB] Error clearing ${storeName}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error(`[IndexedDB] Failed to clear store:`, error);
    }
  }

  /**
   * Clear all caches for current user
   */
  async clearAll(userId?: string): Promise<void> {
    const targetUserId = userId || this.currentUserId;
    // console.log(`[IndexedDB] Clearing all caches for user ${targetUserId}`);

    await Promise.all(
      Object.values(STORES).map((storeName) =>
        this.clearStore(storeName, targetUserId)
      )
    );
  }

  /**
   * Get cache statistics for current user
   */
  async getStats(userId?: string): Promise<Record<string, number>> {
    try {
      const db = await this.ensureDB();
      const targetUserId = userId || this.currentUserId;
      const stats: Record<string, number> = {};

      for (const storeName of Object.values(STORES)) {
        const count = await new Promise<number>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readonly');
          const store = transaction.objectStore(storeName);
          const index = store.index('userId');
          const request = index.count(IDBKeyRange.only(targetUserId));

          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        stats[storeName] = count;
      }

      // console.log(`[IndexedDB] Cache stats for user ${targetUserId}:`, stats);
      return stats;
    } catch (error) {
      console.error(`[IndexedDB] Failed to get stats:`, error);
      return {};
    }
  }

  /**
   * Clean up expired entries across all stores
   */
  async cleanupExpired(): Promise<number> {
    try {
      const db = await this.ensureDB();
      let deletedCount = 0;
      const now = Date.now();

      for (const storeName of Object.values(STORES)) {
        await new Promise<void>((resolve, reject) => {
          const transaction = db.transaction([storeName], 'readwrite');
          const store = transaction.objectStore(storeName);
          const index = store.index('expiresAt');
          const request = index.openCursor(IDBKeyRange.upperBound(now));

          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
            if (cursor) {
              cursor.delete();
              deletedCount++;
              cursor.continue();
            } else {
              resolve();
            }
          };

          request.onerror = () => reject(request.error);
        });
      }

      // console.log(`[IndexedDB] Cleaned up ${deletedCount} expired entries`);
      return deletedCount;
    } catch (error) {
      console.error(`[IndexedDB] Failed to cleanup expired entries:`, error);
      return 0;
    }
  }

  /**
   * Close database connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      // console.log('[IndexedDB] Database connection closed');
    }
  }

  /**
   * Delete the entire database and reinitialize
   */
  async resetDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Close existing connection
      this.close();

      const request = indexedDB.deleteDatabase(DB_NAME);

      request.onsuccess = () => {
        // console.log('[IndexedDB] Database deleted successfully');
        // Reinitialize
        this.initPromise = this.initDB();
        this.initPromise.then(resolve).catch(reject);
      };

      request.onerror = () => {
        console.error('[IndexedDB] Error deleting database:', request.error);
        reject(request.error);
      };

      request.onblocked = () => {
        console.warn('[IndexedDB] Database deletion blocked. Please close all tabs and try again.');
        reject(new Error('Database deletion blocked'));
      };
    });
  }

  /**
   * Check if database is ready
   */
  async isReady(): Promise<boolean> {
    try {
      await this.ensureDB();
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
const indexedDBService = new IndexedDBService();

// Export store names for use in other services
export const INDEXED_DB_STORES = STORES;

export default indexedDBService;
